import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Monitor, ShieldCheck, Lock, Unlock, Settings2, Loader2, Search, Trash2, Send,
  Clock, ChevronDown, ChevronUp, CheckCircle2, XCircle, RefreshCw, Code,
  Terminal, FileText, Eye, ListChecks, SquareCheck, Square, Activity,
  ArrowUpCircle, History, Power, Zap, ShieldAlert, Rocket
} from 'lucide-react';

const MODE_META = {
  USER: { label: 'Benutzer', color: 'bg-green-500', icon: Lock, desc: 'Vollstaendig gesperrt' },
  SERVICE: { label: 'Service', color: 'bg-yellow-500', icon: Settings2, desc: 'Eingeschraenkter Zugang' },
  ADMIN: { label: 'Admin', color: 'bg-red-500', icon: Unlock, desc: 'Voller Zugang' },
  NONE: { label: 'Nicht konfiguriert', color: 'bg-zinc-600', icon: Monitor, desc: 'Kein Kiosk-Modus' },
};

function formatTimeAgo(iso) {
  if (!iso) return '\u2014';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
  return new Date(iso).toLocaleDateString('de-DE');
}

/* ===== Toggle Row Component ===== */
function ToggleRow({ label, desc, checked, onChange, children }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-[#1e1e22] last:border-0 group">
      <div className="flex-1 pr-6">
        <div className="text-[13px] text-white font-medium">{label}</div>
        {desc && <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">{desc}</div>}
      </div>
      <div className="flex items-center gap-3 shrink-0 pt-0.5">
        {children}
        <button onClick={() => onChange(!checked)}
          className={`w-10 h-5 rounded-full transition-colors relative ${checked ? 'bg-[#00a8e8]' : 'bg-zinc-700'}`}
          data-testid={`toggle-${label.replace(/\s+/g, '-').toLowerCase()}`}>
          <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${checked ? 'translate-x-5.5 left-[22px]' : 'left-[2px]'}`} />
        </button>
      </div>
    </div>
  );
}

/* ===== LOCKDOWN CONFIG EDITOR (FrontFace-style) ===== */
function LockdownEditor({ device, config, onSave, onClose }) {
  const [cfg, setCfg] = useState(config);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('boot'); // boot, continuous, security, apply

  const u = (key, val) => setCfg(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/kiosk/config/${device.id}`, cfg);
      onSave();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const TABS = [
    { id: 'boot', label: 'Hoch- & Runterfahren', icon: Power, color: 'text-blue-400' },
    { id: 'continuous', label: 'Dauerbetrieb', icon: Zap, color: 'text-orange-400' },
    { id: 'security', label: 'Sicherheit', icon: ShieldAlert, color: 'text-red-400' },
    { id: 'apply', label: 'Anwenden', icon: Rocket, color: 'text-green-400' },
  ];

  return (
    <div className="bg-[#0e0e10] rounded-xl border border-[#2A2A2A] overflow-hidden" data-testid="kiosk-config-editor">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-[#141416] border-b border-[#2A2A2A]">
        <div className="flex items-center gap-3">
          <Settings2 className="w-5 h-5 text-[#D50C2D]" />
          <span className="text-white font-semibold text-sm">{device.computer_name}</span>
          <span className="text-zinc-600 text-xs">Lockdown Konfiguration</span>
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-white text-xs px-2 py-1 rounded hover:bg-zinc-800">Schliessen</button>
      </div>

      <div className="flex min-h-[500px]">
        {/* Sidebar */}
        <div className="w-52 bg-[#111113] border-r border-[#1e1e22] py-2">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-[12px] font-medium transition-colors ${
                tab === t.id ? 'bg-[#1e1e22] text-white border-l-2 border-[#00a8e8]' : 'text-zinc-500 hover:text-zinc-300 hover:bg-[#1a1a1c] border-l-2 border-transparent'
              }`} data-testid={`lockdown-tab-${t.id}`}>
              <t.icon className={`w-4 h-4 ${tab === t.id ? t.color : 'text-zinc-600'}`} />
              {t.label}
            </button>
          ))}

          {/* Core settings below tabs */}
          <div className="mt-4 pt-3 border-t border-[#1e1e22] px-4 space-y-2">
            <div>
              <label className="text-zinc-600 text-[10px] block mb-0.5">Reader Demo PIN</label>
              <input value={cfg.pin || ''} onChange={e => u('pin', e.target.value)}
                className="w-full h-7 px-2 bg-[#0a0a0b] border border-[#333] rounded text-white text-xs font-mono" data-testid="config-pin" />
            </div>
            <div>
              <label className="text-zinc-600 text-[10px] block mb-0.5">Admin PIN</label>
              <input value={cfg.admin_pin || ''} onChange={e => u('admin_pin', e.target.value)}
                className="w-full h-7 px-2 bg-[#0a0a0b] border border-[#333] rounded text-white text-xs font-mono" data-testid="config-admin-pin" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-5 overflow-y-auto max-h-[500px]">
          <h3 className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider mb-3">
            {TABS.find(t => t.id === tab)?.label} Einstellungen
          </h3>

          {/* TAB: Hoch- & Runterfahren */}
          {tab === 'boot' && (
            <div>
              <ToggleRow label="Automatische Windows-Anmeldung" desc="Automatische Anmeldung ohne Passworteingabe beim Einschalten."
                checked={cfg.auto_login_enabled ?? true} onChange={v => u('auto_login_enabled', v)}>
                <div className="flex gap-1">
                  <input value={cfg.auto_login_user || ''} onChange={e => u('auto_login_user', e.target.value)}
                    placeholder="Benutzer" className="w-20 h-6 px-1.5 bg-[#0a0a0b] border border-[#333] rounded text-[10px] text-white" />
                  <input type="password" value={cfg.auto_login_password || ''} onChange={e => u('auto_login_password', e.target.value)}
                    placeholder="Passwort" className="w-20 h-6 px-1.5 bg-[#0a0a0b] border border-[#333] rounded text-[10px] text-white" />
                </div>
              </ToggleRow>
              <ToggleRow label="Windows Hello deaktivieren" desc="Deaktiviert biometrische Authentifizierung (Fingerabdruck, Gesichtserkennung, PIN). Muss aktiviert sein fuer Auto-Login."
                checked={cfg.disable_windows_hello ?? true} onChange={v => u('disable_windows_hello', v)} />
              <ToggleRow label="Umgehung der automatischen Anmeldung verhindern" desc="Verhindert, dass SHIFT beim Boot die automatische Anmeldung unterbricht."
                checked={cfg.prevent_login_bypass ?? true} onChange={v => u('prevent_login_bypass', v)} />
              <ToggleRow label="Automatische (Neu-)Anmeldung erzwingen" desc="Nach Abmeldung meldet sich Windows sofort wieder mit dem Kiosk-Konto an."
                checked={cfg.force_auto_relogin ?? true} onChange={v => u('force_auto_relogin', v)} />
              <ToggleRow label="Benutzernamen nicht anzeigen" desc="Verhindert die Anzeige von Benutzernamen auf dem Anmeldebildschirm."
                checked={cfg.hide_usernames_login ?? true} onChange={v => u('hide_usernames_login', v)} />
              <ToggleRow label="Sperrbildschirm deaktivieren" desc="Deaktiviert den Windows-Sperrbildschirm vollstaendig."
                checked={cfg.disable_lock_screen ?? true} onChange={v => u('disable_lock_screen', v)} />
              <ToggleRow label="UI-Elemente waehrend des Boots unterdruecken" desc="Unterdrueckt Boot-Logo, Statusanzeige und Statusmeldung."
                checked={cfg.suppress_boot_ui ?? false} onChange={v => u('suppress_boot_ui', v)} />
              <ToggleRow label="Fehleranzeige waehrend des Boots unterdruecken" desc="Unterdrueckt moegliche Fehlermeldungen waehrend des Bootvorgangs."
                checked={cfg.suppress_boot_errors ?? false} onChange={v => u('suppress_boot_errors', v)} />

              <div className="py-3 border-b border-[#1e1e22]">
                <div className="text-[13px] text-white font-medium mb-1.5">Windows Shell</div>
                <div className="text-[11px] text-zinc-500 mb-2">Welche Shell beim Start geladen wird.</div>
                <div className="flex gap-3">
                  {[['explorer', 'Startmenue & Desktop'], ['custom', 'TSR Launcher (Kiosk)']].map(([val, label]) => (
                    <label key={val} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="shell_mode" checked={cfg.shell_mode === val} onChange={() => u('shell_mode', val)}
                        className="accent-[#00a8e8]" />
                      <span className="text-zinc-300 text-[11px]">{label}</span>
                    </label>
                  ))}
                </div>
                {cfg.shell_mode === 'custom' && (
                  <input value={cfg.shell_custom_app || ''} onChange={e => u('shell_custom_app', e.target.value)}
                    placeholder="Pfad zur Anwendung" className="mt-2 w-full h-6 px-2 bg-[#0a0a0b] border border-[#333] rounded text-[10px] text-white font-mono" />
                )}
              </div>

              <ToggleRow label="Automatischer Neustart der Windows-Shell" desc="Startet die Shell automatisch neu, wenn sie unerwartet stoppt."
                checked={cfg.auto_restart_shell ?? true} onChange={v => u('auto_restart_shell', v)} />

              {/* Scheduled tasks */}
              <ToggleRow label="Taegliches Herunterfahren" desc="Schaltet den PC zur angegebenen Zeit automatisch ab."
                checked={cfg.daily_shutdown_enabled ?? false} onChange={v => u('daily_shutdown_enabled', v)}>
                {cfg.daily_shutdown_enabled && (
                  <div className="flex gap-1">
                    <select value={cfg.daily_shutdown_days || 'Mo-Fr'} onChange={e => u('daily_shutdown_days', e.target.value)}
                      className="h-6 px-1 bg-[#0a0a0b] border border-[#333] rounded text-[10px] text-white">
                      <option value="Mo-Fr">Mo - Fr</option><option value="Taeglich">Taeglich</option><option value="Sa-So">Sa - So</option>
                    </select>
                    <input type="time" value={cfg.daily_shutdown_time || '17:00'} onChange={e => u('daily_shutdown_time', e.target.value)}
                      className="h-6 px-1 bg-[#0a0a0b] border border-[#333] rounded text-[10px] text-white" />
                  </div>
                )}
              </ToggleRow>
              <ToggleRow label="Taeglicher (Re)boot" desc="Startet den PC zur angegebenen Zeit automatisch neu."
                checked={cfg.daily_reboot_enabled ?? false} onChange={v => u('daily_reboot_enabled', v)}>
                {cfg.daily_reboot_enabled && (
                  <div className="flex gap-1">
                    <select value={cfg.daily_reboot_days || 'Mo-Fr'} onChange={e => u('daily_reboot_days', e.target.value)}
                      className="h-6 px-1 bg-[#0a0a0b] border border-[#333] rounded text-[10px] text-white">
                      <option value="Mo-Fr">Mo - Fr</option><option value="Taeglich">Taeglich</option><option value="Sa-So">Sa - So</option>
                    </select>
                    <input type="time" value={cfg.daily_reboot_time || '08:00'} onChange={e => u('daily_reboot_time', e.target.value)}
                      className="h-6 px-1 bg-[#0a0a0b] border border-[#333] rounded text-[10px] text-white" />
                  </div>
                )}
              </ToggleRow>
              <ToggleRow label="Taeglicher Ruhezustand" desc="Versetzt den PC zur angegebenen Zeit in den Ruhezustand."
                checked={cfg.daily_hibernate_enabled ?? false} onChange={v => u('daily_hibernate_enabled', v)}>
                {cfg.daily_hibernate_enabled && (
                  <div className="flex gap-1">
                    <select value={cfg.daily_hibernate_days || 'Mo-Fr'} onChange={e => u('daily_hibernate_days', e.target.value)}
                      className="h-6 px-1 bg-[#0a0a0b] border border-[#333] rounded text-[10px] text-white">
                      <option value="Mo-Fr">Mo - Fr</option><option value="Taeglich">Taeglich</option><option value="Sa-So">Sa - So</option>
                    </select>
                    <input type="time" value={cfg.daily_hibernate_time || '17:00'} onChange={e => u('daily_hibernate_time', e.target.value)}
                      className="h-6 px-1 bg-[#0a0a0b] border border-[#333] rounded text-[10px] text-white" />
                  </div>
                )}
              </ToggleRow>
              <ToggleRow label="Kennwortabfrage nach Ruhezustand ueberspringen" desc="Kein Kennwort beim Aufwachen aus dem Ruhezustand."
                checked={cfg.skip_password_after_resume ?? true} onChange={v => u('skip_password_after_resume', v)} />
              <ToggleRow label="Aufwach-Timer zulassen" desc="Erlaubt zeitgesteuerte Aktivierung aus dem Ruhezustand."
                checked={cfg.allow_wake_timers ?? true} onChange={v => u('allow_wake_timers', v)} />
              <ToggleRow label="OOBE Datenschutzeinstellungen deaktivieren" desc="Ueberspringt Datenschutzfragen bei der Ersteinrichtung."
                checked={cfg.disable_oobe_privacy ?? true} onChange={v => u('disable_oobe_privacy', v)} />

              {/* Paths */}
              <div className="mt-4 pt-3 border-t border-[#1e1e22] space-y-2">
                <div className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider mb-2">Anwendungspfade</div>
                <div>
                  <label className="text-zinc-500 text-[10px] block mb-0.5">TSRID.exe Pfad</label>
                  <input value={cfg.tsrid_path || ''} onChange={e => u('tsrid_path', e.target.value)}
                    className="w-full h-7 px-2 bg-[#0a0a0b] border border-[#333] rounded text-white text-[10px] font-mono" />
                </div>
                <div>
                  <label className="text-zinc-500 text-[10px] block mb-0.5">Reader Demo Pfad</label>
                  <input value={cfg.readerdemo_path || ''} onChange={e => u('readerdemo_path', e.target.value)}
                    className="w-full h-7 px-2 bg-[#0a0a0b] border border-[#333] rounded text-white text-[10px] font-mono" />
                </div>
              </div>
            </div>
          )}

          {/* TAB: Dauerbetrieb */}
          {tab === 'continuous' && (
            <div>
              <ToggleRow label="Bildschirmschoner deaktivieren" desc="Deaktiviert den Windows-Bildschirmschoner."
                checked={cfg.disable_screensaver ?? true} onChange={v => u('disable_screensaver', v)} />
              <ToggleRow label="Windows Update deaktivieren" desc="Verhindert automatische Windows-Updates im Hintergrund."
                checked={cfg.disable_windows_update ?? false} onChange={v => u('disable_windows_update', v)} />

              <div className="py-3 border-b border-[#1e1e22]">
                <div className="text-[13px] text-white font-medium">Nutzungszeiten des Systems</div>
                <div className="text-[11px] text-zinc-500 mt-0.5 mb-2">Zeitfenster in dem KEINE Windows Updates installiert werden sollen.</div>
                <div className="flex gap-2">
                  <input type="time" value={cfg.active_hours_start || '07:00'} onChange={e => u('active_hours_start', e.target.value)}
                    className="h-6 px-2 bg-[#0a0a0b] border border-[#333] rounded text-[10px] text-white" />
                  <span className="text-zinc-600 text-[10px] pt-1">bis</span>
                  <input type="time" value={cfg.active_hours_end || '01:00'} onChange={e => u('active_hours_end', e.target.value)}
                    className="h-6 px-2 bg-[#0a0a0b] border border-[#333] rounded text-[10px] text-white" />
                </div>
              </div>

              <ToggleRow label="'Monitor Standby' deaktivieren" desc="Deaktiviert den Ruhezustand/Standby-Modus des Monitors."
                checked={cfg.disable_monitor_standby ?? true} onChange={v => u('disable_monitor_standby', v)} />
              <ToggleRow label="Desktop-Hintergrund auf Schwarz setzen" desc="Entfernt alle Hintergrundbilder und setzt die Farbe auf Schwarz."
                checked={cfg.black_desktop ?? true} onChange={v => u('black_desktop', v)} />
              <ToggleRow label="Energie fuer Hoechstleistungen aktivieren" desc="Setzt den Energiesparplan auf 'Hoechste Leistung'."
                checked={cfg.high_performance_power ?? true} onChange={v => u('high_performance_power', v)} />
              <ToggleRow label="Netzschalter deaktivieren" desc="Verhindert das Herunterfahren ueber den physischen Netzschalter."
                checked={cfg.disable_power_button ?? false} onChange={v => u('disable_power_button', v)} />
              <ToggleRow label="WPF-Hardwarebeschleunigung deaktivieren" desc="Deaktiviert global die Hardwarebeschleunigung fuer WPF-Anwendungen."
                checked={cfg.disable_wpf_hw_accel ?? false} onChange={v => u('disable_wpf_hw_accel', v)} />
              <ToggleRow label="Bluescreens verbergen" desc="Zeigt einen schwarzen Bildschirm statt des 'Blue Screen of Death' bei Systemabstuerzen."
                checked={cfg.hide_bluescreens ?? false} onChange={v => u('hide_bluescreens', v)} />
              <ToggleRow label="Fehlerberichterstattung deaktivieren" desc="Deaktiviert Windows-Fehlermelde-Dialoge bei Anwendungsabstuerzen."
                checked={cfg.disable_error_reporting ?? true} onChange={v => u('disable_error_reporting', v)} />
              <ToggleRow label="Treiber-Updates deaktivieren" desc="Verhindert die Aktualisierung von Geraetetreibern ueber Windows Update."
                checked={cfg.disable_driver_updates ?? false} onChange={v => u('disable_driver_updates', v)} />
              <ToggleRow label="Windows-Funktionsupdates aufschieben" desc="Verschiebt grosse Feature-Updates um 30 Tage."
                checked={cfg.defer_feature_updates ?? false} onChange={v => u('defer_feature_updates', v)} />
              <ToggleRow label="Windows-Qualitaetsupdates aufschieben" desc="Verschiebt Qualitaetsupdates um 30 Tage."
                checked={cfg.defer_quality_updates ?? false} onChange={v => u('defer_quality_updates', v)} />
              <ToggleRow label="Windows-Benachrichtigungszentrale deaktivieren" desc="Deaktiviert das Infocenter und alle Benachrichtigungen."
                checked={cfg.disable_notification_center ?? true} onChange={v => u('disable_notification_center', v)} />
              <ToggleRow label="Windows-Feedback deaktivieren" desc="Deaktiviert Feedback-Popups und -Benachrichtigungen."
                checked={cfg.disable_feedback ?? true} onChange={v => u('disable_feedback', v)} />
              <ToggleRow label="Windows-Suche deaktivieren" desc="Deaktiviert den Windows-Suchindexdienst um die Leistung zu erhoehen."
                checked={cfg.disable_windows_search ?? false} onChange={v => u('disable_windows_search', v)} />
              <ToggleRow label="Cortana deaktivieren" desc="Deaktiviert den virtuellen Assistenten Cortana."
                checked={cfg.disable_cortana ?? true} onChange={v => u('disable_cortana', v)} />
              <ToggleRow label="Microsoft Store deaktivieren" desc="Verhindert die Installation von Apps aus dem Microsoft Store."
                checked={cfg.disable_microsoft_store ?? false} onChange={v => u('disable_microsoft_store', v)} />
              <ToggleRow label="Windows-Bildschirmtastatur aktivieren" desc="Aktiviert die in Windows enthaltene Bildschirmtastatur."
                checked={cfg.enable_onscreen_keyboard ?? false} onChange={v => u('enable_onscreen_keyboard', v)} />
              <ToggleRow label="Kameras deaktivieren" desc="Deaktiviert alle Kameras an diesem Computer."
                checked={cfg.disable_cameras ?? false} onChange={v => u('disable_cameras', v)} />
              <ToggleRow label="Audio deaktivieren" desc="Schaltet alle Audioausgaenge des PCs stumm."
                checked={cfg.disable_audio ?? false} onChange={v => u('disable_audio', v)} />
              <ToggleRow label="Windows-Sounds deaktivieren" desc="Schaltet alle System-Sounds des Betriebssystems aus."
                checked={cfg.disable_windows_sounds ?? false} onChange={v => u('disable_windows_sounds', v)} />
            </div>
          )}

          {/* TAB: Sicherheit */}
          {tab === 'security' && (
            <div>
              <ToggleRow label="Windows-Taste und CTRL+ALT+ENTF deaktivieren" desc="Deaktiviert Windows-Taste, ALT-Taste und STRG-Taste(n), wodurch auch STRG+ALT+ENTF blockiert wird."
                checked={cfg.disable_win_key_cad ?? true} onChange={v => u('disable_win_key_cad', v)} />
              <ToggleRow label="Windows-Firewall aktivieren" desc="Stellt sicher, dass die Windows-Firewall aktiviert ist."
                checked={cfg.enable_firewall ?? true} onChange={v => u('enable_firewall', v)} />
              <ToggleRow label="Schnelle Benutzerumschaltung deaktivieren" desc="Deaktiviert die Moeglichkeit, zwischen Benutzerkonten zu wechseln."
                checked={cfg.disable_fast_user_switching ?? true} onChange={v => u('disable_fast_user_switching', v)} />
              <ToggleRow label="Task-Manager deaktivieren" desc="Deaktiviert die Tastenkombination CTRL+SHIFT+ESC und den Task-Manager."
                checked={cfg.disable_task_manager ?? true} onChange={v => u('disable_task_manager', v)} />
              <ToggleRow label="'Passwort aendern' deaktivieren" desc="Deaktiviert die Option zum Aendern des Kennworts des aktuellen Benutzerkontos."
                checked={cfg.disable_password_change ?? true} onChange={v => u('disable_password_change', v)} />
              <ToggleRow label="'Abmelden' deaktivieren" desc="Entfernt die Option 'Abmelden' vom STRG+ALT+ENTF-Bildschirm."
                checked={cfg.disable_sign_out ?? true} onChange={v => u('disable_sign_out', v)} />
              <ToggleRow label="'Arbeitsstation sperren' deaktivieren" desc="Deaktiviert die Option zum Sperren des Computers."
                checked={cfg.disable_lock_workstation ?? true} onChange={v => u('disable_lock_workstation', v)} />
              <ToggleRow label="Abschaltung deaktivieren" desc="Verhindert, dass ein Benutzer den Computer herunterfahren kann."
                checked={cfg.disable_shutdown_option ?? true} onChange={v => u('disable_shutdown_option', v)} />
              <ToggleRow label="Sticky Keys deaktivieren" desc="Deaktiviert die Einrastfunktion (5x SHIFT), die ein Sicherheitsrisiko bei oeffentlichen Geraeten darstellt."
                checked={cfg.disable_sticky_keys ?? true} onChange={v => u('disable_sticky_keys', v)} />
              <ToggleRow label="Windows-Telemetrie deaktivieren" desc="Deaktiviert die Uebermittlung von Nutzungsdaten an Microsoft."
                checked={cfg.disable_telemetry ?? true} onChange={v => u('disable_telemetry', v)} />
              <ToggleRow label="Autorun/Autoplay deaktivieren" desc="Deaktiviert das Autorun-Verhalten beim Einlegen von USB-Sticks oder CD-ROMs."
                checked={cfg.disable_autorun ?? true} onChange={v => u('disable_autorun', v)} />
              <ToggleRow label="Alle USB-Speichergeraete deaktivieren" desc="Verweigert den Zugriff auf USB-Sticks, SD-Karten oder externe Festplatten."
                checked={cfg.disable_usb_storage ?? false} onChange={v => u('disable_usb_storage', v)} />
              <div className="py-3 border-b border-[#1e1e22]">
                <div className="text-[13px] text-white font-medium">Laufwerke ausblenden/deaktivieren</div>
                <div className="text-[11px] text-zinc-500 mt-0.5 mb-2">Laufwerksbuchstaben die fuer den Benutzer ausgeblendet werden sollen (z.B. "D,E,F").</div>
                <input value={cfg.hide_drives || ''} onChange={e => u('hide_drives', e.target.value)}
                  placeholder="z.B. D,E,F" className="w-48 h-6 px-2 bg-[#0a0a0b] border border-[#333] rounded text-[10px] text-white font-mono" />
              </div>
            </div>
          )}

          {/* TAB: Anwenden */}
          {tab === 'apply' && (
            <div className="space-y-4">
              <div className="bg-[#141416] rounded-lg p-4 border border-[#1e1e22]">
                <h4 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                  <Rocket className="w-4 h-4 text-green-400" />Konfiguration anwenden
                </h4>
                <p className="text-zinc-500 text-[11px] leading-relaxed mb-4">
                  Speichern Sie die Konfiguration und deployen Sie sie anschliessend auf das Geraet.
                  Das Geraet muss online sein und nach dem Deploy neu gestartet werden.
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={save} disabled={saving}
                    className="bg-[#D50C2D] hover:bg-[#B00A26] text-white text-xs" data-testid="config-save-btn">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                    Konfiguration speichern
                  </Button>
                  <Button size="sm" variant="outline" onClick={onClose} className="text-xs border-zinc-700 text-zinc-400">Abbrechen</Button>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-[#141416] rounded-lg p-4 border border-[#1e1e22]">
                <h4 className="text-white text-sm font-semibold mb-3">Zusammenfassung</h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]">
                  <SummaryRow label="Auto-Login" val={cfg.auto_login_enabled ? `${cfg.auto_login_user}` : 'Aus'} />
                  <SummaryRow label="Shell" val={cfg.shell_mode === 'custom' ? 'TSR Launcher' : 'Explorer'} />
                  <SummaryRow label="TSRID Auto-Start" val={cfg.auto_start_tsrid ? 'Ja' : 'Nein'} />
                  <SummaryRow label="Task-Manager" val={cfg.disable_task_manager ? 'Gesperrt' : 'Erlaubt'} />
                  <SummaryRow label="USB-Speicher" val={cfg.disable_usb_storage ? 'Gesperrt' : 'Erlaubt'} />
                  <SummaryRow label="Windows Update" val={cfg.disable_windows_update ? 'Deaktiviert' : 'Aktiv'} />
                  <SummaryRow label="Energieplan" val={cfg.high_performance_power ? 'Hoechstleistung' : 'Standard'} />
                  <SummaryRow label="Telemetrie" val={cfg.disable_telemetry ? 'Deaktiviert' : 'Aktiv'} />
                  <SummaryRow label="Tagl. Shutdown" val={cfg.daily_shutdown_enabled ? `${cfg.daily_shutdown_time}` : 'Aus'} />
                  <SummaryRow label="Tagl. Reboot" val={cfg.daily_reboot_enabled ? `${cfg.daily_reboot_time}` : 'Aus'} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, val }) {
  return (
    <div className="flex justify-between">
      <span className="text-zinc-500">{label}</span>
      <span className="text-white font-medium">{val}</span>
    </div>
  );
}

/* ===== Script Preview Modal ===== */
function ScriptPreview({ deviceId, onClose }) {
  const [script, setScript] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('setup');

  useEffect(() => { loadScript(tab); }, [tab, deviceId]);

  const loadScript = async (type) => {
    setLoading(true);
    try {
      if (type === 'setup') { const res = await api.get(`/kiosk/preview/${deviceId}`); setScript(res.data.script); }
      else if (type === 'hta') { const res = await api.get(`/kiosk/preview-hta/${deviceId}`); setScript(res.data.hta); }
      else { const res = await api.get('/kiosk/preview-watchdog'); setScript(res.data.script); }
    } catch (e) { setScript('Fehler beim Laden...'); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-8" data-testid="script-preview-modal">
      <div className="bg-[#0e0e10] border border-[#2A2A2A] rounded-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-[#2A2A2A]">
          <div className="flex items-center gap-3">
            <Code className="w-5 h-5 text-[#D50C2D]" />
            <h3 className="text-white font-semibold text-sm">Script Vorschau</h3>
            <div className="flex gap-1 ml-4">
              {[['setup', 'Setup Script', Terminal], ['hta', 'HTA Launcher', FileText], ['watchdog', 'Watchdog', Eye]].map(([key, label, Icon]) => (
                <button key={key} onClick={() => setTab(key)}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors ${
                    tab === key ? 'bg-[#D50C2D] text-white' : 'text-zinc-500 hover:text-white hover:bg-zinc-800'
                  }`}><Icon className="w-3 h-3" />{label}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(script)} className="text-[10px] h-7 border-zinc-700 text-zinc-400" data-testid="script-copy-btn">Kopieren</Button>
            <button onClick={onClose} className="text-zinc-500 hover:text-white text-sm">Schliessen</button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading ? <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-zinc-600" /></div>
            : <pre className="text-[11px] leading-relaxed text-zinc-300 font-mono whitespace-pre-wrap">{script}</pre>}
        </div>
      </div>
    </div>
  );
}

/* ===== Deploy Tracker ===== */
function DeployTracker({ onClose }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { try { const r = await api.get('/kiosk/deploy-status?limit=30'); setTasks(r.data.tasks || []); } catch(e){} setLoading(false); })(); }, []);
  const statusIcon = (s) => s === 'completed' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400" /> : s === 'dispatched' ? <ArrowUpCircle className="w-3.5 h-3.5 text-yellow-400" /> : s === 'pending' ? <Clock className="w-3.5 h-3.5 text-blue-400" /> : <XCircle className="w-3.5 h-3.5 text-zinc-500" />;
  const statusColor = (s) => s === 'completed' ? 'text-green-400' : s === 'dispatched' ? 'text-yellow-400' : s === 'pending' ? 'text-blue-400' : 'text-zinc-500';
  return (
    <div className="bg-[#1C1C1C] rounded-xl border border-[#2A2A2A] p-5 mb-4" data-testid="deploy-tracker">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2"><ListChecks className="w-4 h-4 text-[#D50C2D]" />Deployment Status</h3>
        <button onClick={onClose} className="text-zinc-500 hover:text-white text-xs">Schliessen</button>
      </div>
      {loading ? <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-zinc-600" /></div>
        : tasks.length === 0 ? <p className="text-zinc-600 text-xs text-center py-4">Keine Deployments</p>
        : <div className="space-y-1.5 max-h-64 overflow-y-auto">{tasks.map(t => (
          <div key={t.id} className="flex items-center gap-3 px-3 py-2 bg-[#141416] rounded-lg text-xs">
            {statusIcon(t.status)}<span className={`font-medium ${statusColor(t.status)}`}>{t.status}</span>
            <span className="text-zinc-400 flex-1">{t.description || 'Task'}</span>
            <span className="text-zinc-600">{t.target_device_ids?.length || 0} Geraet(e)</span>
            <span className="text-zinc-700">{formatTimeAgo(t.created_at)}</span>
          </div>
        ))}</div>}
    </div>
  );
}

/* ===== Activity Feed ===== */
function ActivityFeed({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { try { const r = await api.get('/kiosk/activity?limit=30'); setLogs(r.data.logs || []); } catch(e){} setLoading(false); })(); }, []);
  const meta = { deploy: { label: 'Deploy', color: 'text-green-400', icon: Send }, mode_switch: { label: 'Modus', color: 'text-yellow-400', icon: Settings2 }, remote_unlock: { label: 'Unlock', color: 'text-orange-400', icon: Unlock }, remove: { label: 'Entfernt', color: 'text-red-400', icon: Trash2 } };
  return (
    <div className="bg-[#1C1C1C] rounded-xl border border-[#2A2A2A] p-5 mb-4" data-testid="activity-feed">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-[#D50C2D]" />Aktivitaeten</h3>
        <button onClick={onClose} className="text-zinc-500 hover:text-white text-xs">Schliessen</button>
      </div>
      {loading ? <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-zinc-600" /></div>
        : logs.length === 0 ? <p className="text-zinc-600 text-xs text-center py-4">Keine Aktivitaeten</p>
        : <div className="space-y-1.5 max-h-64 overflow-y-auto">{logs.map(l => {
          const m = meta[l.action] || { label: l.action, color: 'text-zinc-400', icon: Monitor }; const I = m.icon;
          return <div key={l.id} className="flex items-center gap-3 px-3 py-2 bg-[#141416] rounded-lg text-xs">
            <I className={`w-3.5 h-3.5 ${m.color}`} /><span className={`font-medium ${m.color}`}>{m.label}</span>
            <span className="text-white">{l.computer_name || l.device_id}</span>
            {l.mode && <Badge className="bg-zinc-700 text-zinc-300 text-[9px]">{l.mode}</Badge>}
            {l.bulk && <Badge className="bg-purple-500/20 text-purple-400 text-[9px]">Bulk</Badge>}
            <span className="flex-1" /><span className="text-zinc-700">{formatTimeAgo(l.timestamp)}</span>
          </div>;
        })}</div>}
    </div>
  );
}

/* ===== Device Card ===== */
function DeviceCard({ device, selected, onSelect, onConfigure, onDeploy, onRemove, onModeSwitch, onUnlock, onPreview }) {
  const m = MODE_META[device.kiosk_mode] || MODE_META.NONE;
  const ModeIcon = m.icon;
  const [showActions, setShowActions] = useState(false);
  const [switching, setSwitching] = useState(null);
  const switchMode = async (mode) => { setSwitching(mode); await onModeSwitch(device.id, mode); setSwitching(null); };

  return (
    <div className={`bg-[#1C1C1C] rounded-xl border transition-colors ${selected ? 'border-[#D50C2D]/60 bg-[#D50C2D]/5' : device.kiosk_enabled ? 'border-[#2A2A2A]' : 'border-zinc-800/50'}`}
      data-testid={`kiosk-device-${device.id}`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => onSelect(device.id)} className="text-zinc-600 hover:text-white" data-testid={`kiosk-select-${device.id}`}>
              {selected ? <SquareCheck className="w-4 h-4 text-[#D50C2D]" /> : <Square className="w-4 h-4" />}
            </button>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${device.kiosk_enabled ? 'bg-[#D50C2D]/10' : 'bg-zinc-800'}`}>
              <ModeIcon className={`w-5 h-5 ${device.kiosk_enabled ? 'text-[#D50C2D]' : 'text-zinc-600'}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${device.is_online ? 'bg-green-400 shadow-[0_0_6px_#4ade8060]' : 'bg-red-400'}`} />
                <span className="text-white font-medium text-sm">{device.computer_name}</span>
                {device.tenant_code && <span className="text-zinc-700 text-[10px]">{device.tenant_code}</span>}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge className={`${m.color} text-white text-[9px] px-1.5 py-0`}>{m.label}</Badge>
                <span className="text-zinc-600 text-[10px]">{m.desc}</span>
                {device.last_mode_change && <span className="text-zinc-700 text-[10px] flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{formatTimeAgo(device.last_mode_change)}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {device.kiosk_enabled && <>
              <Button size="sm" variant="outline" onClick={() => onUnlock(device.id)} className="text-[10px] h-7 border-yellow-500/20 text-yellow-400 hover:bg-yellow-500/10" data-testid="kiosk-unlock-btn"><Unlock className="w-3 h-3 mr-1" />Unlock</Button>
              <Button size="sm" variant="outline" onClick={() => setShowActions(!showActions)} className="text-[10px] h-7 border-zinc-700 text-zinc-400">{showActions ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}</Button>
            </>}
            <Button size="sm" variant="outline" onClick={() => onPreview(device.id)} className="text-[10px] h-7 border-zinc-700 text-zinc-400" data-testid="kiosk-preview-btn"><Code className="w-3 h-3 mr-1" />Script</Button>
            <Button size="sm" variant="outline" onClick={() => onConfigure(device)} className="text-[10px] h-7 border-zinc-700 text-zinc-400" data-testid="kiosk-config-btn"><Settings2 className="w-3 h-3 mr-1" />Config</Button>
            {!device.kiosk_enabled
              ? <Button size="sm" onClick={() => onDeploy(device.id)} className="text-[10px] h-7 bg-[#D50C2D] hover:bg-[#B00A26] text-white" data-testid="kiosk-deploy-btn"><Send className="w-3 h-3 mr-1" />Deploy</Button>
              : <Button size="sm" variant="outline" onClick={() => onRemove(device.id)} className="text-[10px] h-7 border-red-500/20 text-red-400 hover:bg-red-500/10" data-testid="kiosk-remove-btn"><Trash2 className="w-3 h-3 mr-1" />Remove</Button>}
          </div>
        </div>
        {showActions && device.kiosk_enabled && (
          <div className="mt-3 pt-3 border-t border-[#2A2A2A] flex gap-2">
            {['USER', 'SERVICE', 'ADMIN'].map(mode => {
              const mm = MODE_META[mode]; const I = mm.icon; const active = device.kiosk_mode === mode;
              return <Button key={mode} size="sm" variant={active ? 'default' : 'outline'}
                className={`text-[10px] h-7 ${active ? 'bg-zinc-700 text-white' : 'border-zinc-700 text-zinc-500'}`}
                disabled={active || switching === mode} onClick={() => switchMode(mode)}>
                {switching === mode ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <I className="w-3 h-3 mr-1" />}{mm.label}
              </Button>;
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ======================== MAIN PAGE ======================== */
export default function KioskPage() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [configDevice, setConfigDevice] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showPreview, setShowPreview] = useState(null);
  const [showTracker, setShowTracker] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [filterMode, setFilterMode] = useState('ALL');
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetchDevices = useCallback(async () => { try { const r = await api.get('/kiosk/status'); setDevices(r.data.devices || []); } catch(e){} setLoading(false); }, []);
  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const openConfig = async (device) => { try { const r = await api.get(`/kiosk/config/${device.id}`); setConfigData(r.data); setConfigDevice(device); } catch(e){} };
  const deploy = async (id) => { if (!window.confirm('Kiosk-Modus deployen? (Neustart erforderlich)')) return; try { await api.post(`/kiosk/deploy/${id}`); fetchDevices(); } catch(e){} };
  const remove = async (id) => { if (!window.confirm('Kiosk-Modus entfernen?')) return; try { await api.post(`/kiosk/remove/${id}`); fetchDevices(); } catch(e){} };
  const modeSwitch = async (id, mode) => { try { await api.post(`/kiosk/mode/${id}`, { mode }); fetchDevices(); } catch(e){} };
  const unlock = async (id) => { const m = window.prompt('Admin-Zugang fuer wie viele Minuten?', '15'); if (!m) return; try { await api.post(`/kiosk/unlock/${id}`, { timeout_minutes: parseInt(m) }); fetchDevices(); } catch(e){} };

  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => selectedIds.size === filtered.length ? setSelectedIds(new Set()) : setSelectedIds(new Set(filtered.map(d => d.id)));

  const bulkDeploy = async () => { if (!selectedIds.size || !window.confirm(`Kiosk auf ${selectedIds.size} Geraet(en) deployen?`)) return; setBulkLoading(true); try { await api.post('/kiosk/bulk-deploy', { device_ids: [...selectedIds] }); setSelectedIds(new Set()); fetchDevices(); } catch(e){} setBulkLoading(false); };
  const bulkMode = async (mode) => { if (!selectedIds.size || !window.confirm(`Modus auf ${mode} fuer ${selectedIds.size} Geraet(e)?`)) return; setBulkLoading(true); try { await api.post('/kiosk/bulk-mode', { device_ids: [...selectedIds], mode }); setSelectedIds(new Set()); fetchDevices(); } catch(e){} setBulkLoading(false); };

  const filtered = devices.filter(d => {
    const ms = !search || d.computer_name?.toLowerCase().includes(search.toLowerCase());
    const mf = filterMode === 'ALL' || (filterMode === 'ACTIVE' && d.kiosk_enabled) || (filterMode === 'INACTIVE' && !d.kiosk_enabled) || d.kiosk_mode === filterMode;
    return ms && mf;
  });

  const kioskEnabled = devices.filter(d => d.kiosk_enabled);
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#D50C2D]" /></div>;

  return (
    <div data-testid="kiosk-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-[#D50C2D]" />Device Mode</h1>
          <p className="text-zinc-500 text-sm mt-1">{kioskEnabled.length} von {devices.length} Geraeten im Kiosk-Modus</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suche..."
              className="h-8 pl-8 pr-3 bg-[#141416] border border-[#2A2A2A] rounded text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#D50C2D] w-44" data-testid="kiosk-search" />
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowTracker(!showTracker)} className={`h-8 text-xs border-zinc-700 ${showTracker ? 'text-[#D50C2D] border-[#D50C2D]/30' : 'text-zinc-400'}`} data-testid="kiosk-tracker-btn"><ListChecks className="w-3.5 h-3.5 mr-1" />Status</Button>
          <Button size="sm" variant="outline" onClick={() => setShowActivity(!showActivity)} className={`h-8 text-xs border-zinc-700 ${showActivity ? 'text-[#D50C2D] border-[#D50C2D]/30' : 'text-zinc-400'}`} data-testid="kiosk-activity-btn"><History className="w-3.5 h-3.5 mr-1" />Log</Button>
          <Button size="sm" variant="outline" onClick={fetchDevices} className="h-8 border-zinc-700 text-zinc-400"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Kiosk Aktiv', value: kioskEnabled.length, color: 'text-[#D50C2D]', icon: ShieldCheck },
          { label: 'USER Modus', value: devices.filter(d => d.kiosk_mode === 'USER').length, color: 'text-green-400', icon: Lock },
          { label: 'SERVICE', value: devices.filter(d => d.kiosk_mode === 'SERVICE').length, color: 'text-yellow-400', icon: Settings2 },
          { label: 'ADMIN', value: devices.filter(d => d.kiosk_mode === 'ADMIN').length, color: 'text-red-400', icon: Unlock },
        ].map(s => (
          <div key={s.label} className="bg-[#141416] border border-[#1e1e22] rounded-lg p-3 flex items-center gap-3">
            <s.icon className={`w-5 h-5 ${s.color}`} /><div><div className={`text-xl font-bold ${s.color}`}>{s.value}</div><div className="text-[10px] text-zinc-600 uppercase tracking-wider">{s.label}</div></div>
          </div>
        ))}
      </div>

      {showTracker && <DeployTracker onClose={() => setShowTracker(false)} />}
      {showActivity && <ActivityFeed onClose={() => setShowActivity(false)} />}

      {/* Lockdown Config Editor */}
      {configDevice && configData && (
        <div className="mb-4">
          <LockdownEditor device={configDevice} config={configData}
            onSave={() => { setConfigDevice(null); fetchDevices(); }} onClose={() => setConfigDevice(null)} />
        </div>
      )}

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="bg-[#D50C2D]/10 border border-[#D50C2D]/30 rounded-xl p-3 mb-4 flex items-center justify-between" data-testid="bulk-action-bar">
          <span className="text-white text-sm font-medium">{selectedIds.size} Geraet(e) ausgewaehlt</span>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={bulkDeploy} disabled={bulkLoading} className="text-[10px] h-7 bg-[#D50C2D] hover:bg-[#B00A26] text-white" data-testid="bulk-deploy-btn">
              {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}Bulk Deploy</Button>
            {['USER', 'SERVICE', 'ADMIN'].map(mm => (
              <Button key={mm} size="sm" variant="outline" onClick={() => bulkMode(mm)} disabled={bulkLoading}
                className="text-[10px] h-7 border-zinc-700 text-zinc-400" data-testid={`bulk-mode-${mm.toLowerCase()}-btn`}>{mm}</Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())} className="text-[10px] h-7 border-zinc-700 text-zinc-400">Abwaehlen</Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-3">
        <button onClick={selectAll} className="text-zinc-600 hover:text-white" data-testid="kiosk-select-all">
          {selectedIds.size === filtered.length && filtered.length > 0 ? <SquareCheck className="w-4 h-4 text-[#D50C2D]" /> : <Square className="w-4 h-4" />}
        </button>
        <span className="text-zinc-600 text-[10px] mr-2">Alle</span>
        {['ALL', 'ACTIVE', 'INACTIVE', 'USER', 'SERVICE', 'ADMIN'].map(mm => (
          <button key={mm} onClick={() => setFilterMode(mm)}
            className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${filterMode === mm ? 'bg-[#D50C2D]/20 text-[#D50C2D]' : 'text-zinc-600 hover:text-zinc-300 hover:bg-zinc-800'}`}
            data-testid={`filter-${mm.toLowerCase()}`}>{mm === 'ALL' ? 'Alle' : mm === 'ACTIVE' ? 'Aktiv' : mm === 'INACTIVE' ? 'Inaktiv' : mm}</button>
        ))}
        <span className="flex-1" /><span className="text-zinc-700 text-[10px]">{filtered.length} Geraete</span>
      </div>

      {/* Devices */}
      <div className="space-y-2">
        {filtered.map(d => <DeviceCard key={d.id} device={d} selected={selectedIds.has(d.id)} onSelect={toggleSelect}
          onConfigure={openConfig} onDeploy={deploy} onRemove={remove} onModeSwitch={modeSwitch} onUnlock={unlock} onPreview={(id) => setShowPreview(id)} />)}
        {filtered.length === 0 && <div className="text-center py-12 text-zinc-600 text-sm">Keine Geraete gefunden</div>}
      </div>

      {showPreview && <ScriptPreview deviceId={showPreview} onClose={() => setShowPreview(null)} />}
    </div>
  );
}
